import type { MButtonProps as ButtonProps } from '@mts/components/Button';
import type { ModalProps } from '@mts/components/Modal';
import type { TableActionType } from '@mts/components/Table';
import type { RowProps } from 'ant-design-vue/es/grid/Row';
import type { CSSProperties, PropType } from 'vue';
import { Fn } from '@mts-fe/types';
import { DAY_FORMAT, propTypes } from '@mts-fe/utils';
import type { FieldMapToTime, FormSchema } from './types/form';
import type { ColEx } from './types';

export const basicProps = {
  model: {
    type: Object as PropType<Recordable>,
    default: () => ({}),
  },
  // 标签宽度  固定宽度
  labelWidth: {
    type: [Number, String] as PropType<number | string>,
    default: 0,
  },
  fieldMapToTime: {
    type: Array as PropType<FieldMapToTime>,
    default: () => [],
  },
  inset: propTypes.bool.def(false),
  compact: propTypes.bool.def(false),
  hideLabel: propTypes.bool.def(false),
  // 表单配置规则
  schemas: {
    type: Array as PropType<FormSchema[]>,
    default: () => [],
  },
  mergeDynamicData: {
    type: Object as PropType<Recordable>,
    default: null,
  },
  baseRowStyle: {
    type: Object as PropType<CSSProperties>,
  },
  baseColProps: {
    type: Object as PropType<Partial<ColEx>>,
  },
  // 区域级每行列数（来自低代码 area.columnSpan），PopoverSearch 用于计算等宽栅格
  columnSpan: propTypes.number,
  strongRelate: propTypes.bool.def(true),
  autoSetPlaceHolder: propTypes.bool.def(true),
  // 在INPUT组件上单击回车时，是否自动提交
  autoSubmitOnEnter: propTypes.bool,
  submitOnReset: propTypes.bool,
  size: propTypes.oneOf(['default', 'small', 'large']).def('default'),
  // 禁用表单
  disabled: propTypes.bool,
  emptySpan: {
    type: [Number, Object] as PropType<number | Recordable>,
    default: 0,
  },
  // 是否显示收起展开按钮
  showAdvancedButton: propTypes.bool,
  // 转化时间
  transformDateFunc: {
    type: Function as PropType<Fn>,
    default: (date: any, format?: string) => {
      return date?.format?.(format || DAY_FORMAT.DTS) ?? date;
    },
  },
  rulesMessageJoinLabel: propTypes.bool.def(true),
  // 超过3行自动折叠
  autoAdvancedLine: propTypes.number.def(3),
  // 不受折叠影响的行数
  alwaysShowLines: propTypes.number.def(1),

  // 是否显示操作按钮
  showActionButtonGroup: propTypes.bool,
  // 操作列Col配置
  actionColOptions: Object as PropType<Partial<ColEx>>,
  // 显示重置按钮
  showResetButton: propTypes.bool.def(true),
  // 是否在enter时自动聚焦下一个控件
  enterFocus: propTypes.bool,
  // 是否在扫码后自动聚焦下一个控件
  scanFocusNext: propTypes.bool,
  // 重置按钮配置
  resetButtonOptions: Object as PropType<Partial<ButtonProps>>,

  // 显示确认按钮
  showSubmitButton: propTypes.bool.def(true),
  // 确认按钮配置
  submitButtonOptions: Object as PropType<Partial<ButtonProps>>,

  // 更多筛选按钮
  showMoreButton: propTypes.bool.def(false),
  // 更多筛选按钮配置
  moreButtonOptions: Object as PropType<Partial<ButtonProps>>,
  // 更多筛选弹窗配置
  moreModalProps: Object as PropType<Partial<ModalProps>>,

  // 自定义重置函数
  resetFunc: Function as PropType<() => Promise<void>>,
  submitFunc: Function as PropType<() => Promise<void>>,

  readonlyMode: propTypes.bool,
  /** @deprecated 行内字段后显示分割线 */
  inlineTextSplit: propTypes.bool,
  // 以下为默认props
  hideRequiredMark: propTypes.bool,

  labelCol: Object as PropType<Partial<ColEx>>,

  layout: propTypes.oneOf(['horizontal', 'vertical', 'inline']).def('vertical'),
  tableAction: {
    type: Object as PropType<TableActionType>,
  },

  wrapperCol: Object as PropType<Partial<ColEx>>,

  colon: propTypes.bool,

  labelAlign: propTypes.string.def('right'),

  rowProps: {
    type: Object as PropType<RowProps>,
    default: { gutter: 12 },
  },
  pinMax: [Number, Boolean] as PropType<number | false>,
};
